import { useTranslation } from '@silospot/i18n/react';
import { Link2, Megaphone } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Card } from './kit';

/**
 * Neutral empty state for the advertising pages when the workspace is connected
 * but no Google Ads account is linked to the active site. Never fakes data —
 * points the user to Settings to link an account.
 */
export function GoogleAdsNoLinkCard({ projectId }: { projectId: string | null }): ReactElement {
  const { t } = useTranslation('pages');
  return (
    <Card className="mx-auto max-w-md py-10 text-center">
      <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-accent-bg text-primary">
        <Link2 className="h-6 w-6" />
      </span>
      <div className="text-[16px] font-bold text-text">{t('gadsp.noLinkedTitle')}</div>
      <p className="mx-auto mt-1 max-w-xs text-[13px] text-text-2">{t('gadsp.noLinkedBody')}</p>
      {projectId ? (
        <Link
          to={`/projects/${projectId}/settings`}
          className="mt-4 inline-flex items-center gap-1.5 rounded-[9px] bg-primary px-3.5 py-2 text-[13px] font-semibold text-white hover:opacity-90"
        >
          {t('gadsp.goToSettings')}
        </Link>
      ) : null}
    </Card>
  );
}

/**
 * Neutral empty state when an account IS linked but NO campaign is monitored yet
 * (C-fix3). The advertising pages read the monitored set, not the whole account —
 * so they point the user to "Manage campaigns" rather than faking data.
 */
export function GoogleAdsNoWatchedCard({ projectId }: { projectId: string | null }): ReactElement {
  const { t } = useTranslation('pages');
  return (
    <Card className="mx-auto max-w-md py-10 text-center">
      <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-accent-bg text-primary">
        <Megaphone className="h-6 w-6" />
      </span>
      <div className="text-[16px] font-bold text-text">{t('gadsp.noWatchedTitle')}</div>
      <p className="mx-auto mt-1 max-w-xs text-[13px] text-text-2">{t('gadsp.noWatchedBody')}</p>
      {projectId ? (
        <Link
          to={`/projects/${projectId}/ads/manage`}
          className="mt-4 inline-flex items-center gap-1.5 rounded-[9px] bg-primary px-3.5 py-2 text-[13px] font-semibold text-white hover:opacity-90"
        >
          {t('gadsp.manageCampaigns')}
        </Link>
      ) : null}
    </Card>
  );
}
